import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChefHat, Calendar, Utensils, Users, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

const ServicesPage = () => {
  const services = [
    {
      id: 'catering',
      icon: <ChefHat className="w-8 h-8" />,
      title: "Catering Marino",
      description: "Servicio de catering especializado en mariscos frescos y ostras de la Patagonia. Perfecto para eventos corporativos y celebraciones.",
      color: "from-ocean-500 to-ocean-600",
      bgColor: "from-ocean-50 to-ocean-100",
      features: ["Menús personalizados", "Chef especializado", "Servicio completo"],
      route: "/servicios/catering"
    },
    {
      id: 'eventos',
      icon: <Calendar className="w-8 h-8" />,
      title: "Eventos Privados",
      description: "Experiencias gastronómicas únicas para eventos privados, bodas y celebraciones especiales con nuestro Oyster Bar móvil.",
      color: "from-jade-500 to-jade-600", 
      bgColor: "from-jade-50 to-jade-100",
      features: ["Oyster Bar móvil", "Eventos personalizados", "Experiencia premium"],
      route: "/servicios/eventos"
    },
    {
      id: 'menu-degustacion',
      icon: <Utensils className="w-8 h-8" />,
      title: "Menú Degustación",
      description: "Menús de degustación cuidadosamente diseñados para descubrir los sabores más auténticos del mar patagónico.",
      color: "from-seagreen-500 to-seagreen-600",
      bgColor: "from-seagreen-50 to-seagreen-100", 
      features: ["Maridajes incluidos", "Productos premium", "Experiencia completa"],
      route: "/servicios/menu-degustacion"
    },
    {
      id: 'consultoria',
      icon: <Users className="w-8 h-8" />,
      title: "Consultoría Gastronómica",
      description: "Asesoramiento profesional para restaurantes y chefs que desean incorporar mariscos premium en sus menús.",
      color: "from-coral-500 to-coral-600",
      bgColor: "from-coral-50 to-coral-100",
      features: ["Asesoramiento experto", "Capacitación", "Desarrollo de menús"],
      route: "/servicios/consultoria"
    }
  ];

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gradient-to-br from-pearl-50/50 via-white to-ocean-25/30">
        {/* Hero Section */}
        <motion.div
          className="relative pt-20 py-16 px-4 sm:px-6 lg:px-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
        <div className="max-w-7xl mx-auto text-center">
          <motion.h1 
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-transparent bg-gradient-to-r from-ocean-600 to-jade-600 bg-clip-text mb-6 font-heading"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Nuestros Servicios
          </motion.h1>
          <motion.p 
            className="text-xl text-deep-600 max-w-3xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Experiencias gastronómicas premium con los mejores mariscos de la Patagonia Argentina. 
            Llevamos el sabor auténtico del mar a tu mesa.
          </motion.p>
        </div>
      </motion.div>

      {/* Services Grid */}
      <motion.div
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.6 }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
              whileHover={{ y: -8, transition: { duration: 0.3 } }}
            >
              <Card className="h-full bg-white/90 backdrop-blur-sm border border-white/60 shadow-lg hover:shadow-2xl transition-all duration-500 rounded-2xl overflow-hidden group">
                <CardHeader className={`bg-gradient-to-r ${service.bgColor} relative`}>
                  <div className="flex items-center gap-4">
                    <div className={`w-16 h-16 bg-gradient-to-r ${service.color} rounded-2xl flex items-center justify-center text-white shadow-lg`}>
                      {service.icon}
                    </div>
                    <CardTitle className="text-2xl font-bold text-deep-700 group-hover:text-ocean-600 transition-colors duration-300">
                      {service.title}
                    </CardTitle>
                  </div>
                </CardHeader>
                
                <CardContent className="p-6 space-y-4">
                  <p className="text-deep-600 text-base leading-relaxed">
                    {service.description}
                  </p>
                  
                  <div className="space-y-2">
                    <h4 className="font-semibold text-deep-700 text-sm">Características:</h4>
                    <ul className="space-y-1">
                      {service.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center text-sm text-deep-600">
                          <div className="w-1.5 h-1.5 bg-jade-500 rounded-full mr-3"></div>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="pt-4">
                    <Link to={service.route}>
                      <Button 
                        className={`w-full bg-gradient-to-r ${service.color} hover:opacity-90 text-white rounded-xl py-3 font-semibold transition-all duration-300 group-hover:scale-105`}
                      >
                        Ver Detalles
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Quick Navigation */}
        <motion.div
          className="mt-16 bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-white/60 shadow-lg"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.2 }}
        >
          <h3 className="text-2xl font-bold text-deep-700 text-center mb-6">
            Navegación Rápida entre Servicios
          </h3>
          <p className="text-deep-600 text-center mb-6 max-w-2xl mx-auto">
            Una vez que ingreses a cualquier servicio específico, podrás navegar fácilmente entre todos nuestros servicios usando los botones de navegación.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {services.map((service, index) => (
              <Link key={service.id} to={service.route}>
                <motion.div
                  className={`px-4 py-2 rounded-full text-sm font-medium bg-gradient-to-r ${service.color} text-white shadow-md hover:shadow-lg transition-all duration-300`}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: 1.4 + index * 0.1 }}
                >
                  {service.title}
                </motion.div>
              </Link>
            ))}
          </div>
        </motion.div>
      </motion.div>
      </div>
    </>
  );
};

export default ServicesPage;
